/**
 * Cookbook few-shot grounding.
 *
 * On strategy-GENERATION requests, a few category-matched cookbook recipes are
 * injected into the system prompt as exemplars — never all 45. The 45 validated
 * recipes stay the single source of truth (this module reads
 * `cookbookRecipesByCategory`; no recipe text is copied).
 *
 * Exemplars are the canonical recipe serialization the model itself emits, so
 * the examples and the required output format are byte-identical. Design:
 * docs/superpowers/specs/2026-07-01-backtester-ai-h2-design.md item 4.
 */
import { cookbookRecipesByCategory } from './index'
import type { CookbookEntry } from './types'
import { recipeWithTradeManager } from './recipe-with-trade-manager'
import { estimateTokens } from '@/features/backtest/graph-engine/catalog'

/** Hard cap on injected exemplar tokens per request (spec item 4). */
export const GROUNDING_TOKEN_BUDGET = 900

/**
 * Deterministic priority order for the 7 cookbook categories. Used to break
 * ties when a request scores equally across categories and to order the
 * default fallback set.
 */
const CATEGORY_PRIORITY = [
  'momentum',
  'indicators',
  'exit-risk',
  'squeeze-trend',
  'structure',
  'timing-volume',
  'filters',
] as const

/**
 * Keyword → category map. Lowercase substring matches against the request text.
 * A category's score is the number of its keywords that appear in the request;
 * the highest-scoring category supplies the exemplars. Kept intentionally small
 * and specific — broad words (e.g. "volume") are steered to the category where
 * they most discriminate.
 */
const CATEGORY_KEYWORDS: Record<string, string[]> = {
  momentum: [
    'momentum',
    'breakout',
    'break out',
    'opening range',
    'orb',
    'gap up',
    'gapper',
    'surge',
    'thrust',
    'relative strength',
    'high of day',
  ],
  'exit-risk': [
    'exit',
    'stop loss',
    'stop-loss',
    'trailing stop',
    'trailing',
    'take profit',
    'profit target',
    'risk',
    'scale out',
    'partial',
    'breakeven',
    'protect',
  ],
  indicators: [
    'indicator',
    'rsi',
    'macd',
    'moving average',
    'sma',
    'ema',
    'bollinger',
    'atr',
    'stochastic',
    'vwap',
    'adx',
    'crossover',
    'cross over',
    'oscillator',
  ],
  'squeeze-trend': [
    'squeeze',
    'keltner',
    'consolidat',
    'compression',
    'ttm',
    'trend follow',
    'trend-follow',
    'volatility contraction',
  ],
  structure: [
    'support',
    'resistance',
    'pivot',
    'swing',
    'higher high',
    'lower low',
    'trendline',
    'channel',
    'price level',
    'reversal',
    'pullback',
  ],
  'timing-volume': [
    'time of day',
    'first hour',
    'power hour',
    'session',
    'rvol',
    'relative volume',
    'volume spike',
    'time filter',
    'intraday timing',
  ],
  filters: [
    'filter',
    'liquidity',
    'float',
    'market cap',
    'universe',
    'price filter',
    'minimum volume',
    'min volume',
    'dollar volume',
  ],
}

/** The default exemplar set when the request matches no category keywords. */
function defaultRecipes(): CookbookEntry[] {
  const picks: CookbookEntry[] = []
  const momentum = cookbookRecipesByCategory['momentum']?.[0]
  const indicators = cookbookRecipesByCategory['indicators']?.[0]
  if (momentum) picks.push(momentum)
  if (indicators) picks.push(indicators)
  return picks
}

/** Rank categories by keyword-match score; deterministic priority tie-break. */
function rankCategories(intentText: string): string[] {
  const text = intentText.toLowerCase()
  const scored = CATEGORY_PRIORITY.map((category) => {
    const keywords = CATEGORY_KEYWORDS[category] ?? []
    const score = keywords.reduce((n, kw) => (text.includes(kw) ? n + 1 : n), 0)
    return { category, score }
  })
  return scored
    .filter((s) => s.score > 0)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score
      return CATEGORY_PRIORITY.indexOf(a.category) - CATEGORY_PRIORITY.indexOf(b.category)
    })
    .map((s) => s.category)
}

/**
 * Pick 2–3 category-matched cookbook recipes for a strategy-generation request.
 *
 * Deterministic: same input → same recipes. Draws from the best-matching
 * category first, spilling into the next-best only if the best has fewer than
 * `n`. Falls back to one momentum + one indicators recipe when nothing matches.
 *
 * Budget-aware: if the serialized picks exceed `budget` tokens, the last
 * (lowest-relevance) recipe is dropped until they fit — so an over-large
 * category's n=3 pick degrades to n=2 on its own.
 */
export function selectGroundingRecipes(
  intentText: string,
  n = 3,
  budget = GROUNDING_TOKEN_BUDGET,
): CookbookEntry[] {
  const ranked = rankCategories(intentText)

  let pool: CookbookEntry[]
  if (ranked.length === 0) {
    pool = defaultRecipes()
  } else {
    pool = []
    for (const category of ranked) {
      for (const entry of cookbookRecipesByCategory[category] ?? []) {
        pool.push(entry)
        if (pool.length >= n) break
      }
      if (pool.length >= n) break
    }
  }

  let picks = pool.slice(0, n)
  while (picks.length > 1 && estimateTokens(serializeGroundingRecipes(picks)) > budget) {
    picks = picks.slice(0, picks.length - 1)
  }
  return picks
}

/**
 * Serialize exemplars into the injected block: a one-line header per recipe
 * (name + category) followed by the canonical `JSON.stringify(recipe)` — the
 * exact form the model emits, so examples and output format are identical.
 * Each template carries its Trade Manager step (its settings and the socket
 * it feeds), the vocabulary the AI builds with (recipeWithTradeManager).
 */
export function serializeGroundingRecipes(entries: CookbookEntry[]): string {
  if (entries.length === 0) return ''
  const blocks = entries.map(
    (entry) =>
      `# ${entry.name} (${entry.category})\n${JSON.stringify(recipeWithTradeManager(entry))}`,
  )
  return `## example strategies (canonical recipe format — match this structure)\n${blocks.join(
    '\n\n',
  )}`
}

/** Context the prompt assembler passes when deciding whether to inject grounding. */
export interface GroundingContext {
  /** Active + indexed tool names for the turn. */
  toolNames: string[]
  /** Classify intent: "create" | "modify" | "read" | null. */
  intent?: string | null
  /** The user's raw request text — drives category matching. */
  intentText: string
}

/**
 * Gate + build the grounding block for a turn. Returns '' (zero cost) unless
 * the turn is a strategy-GENERATION request: classify intent is "create" AND
 * the strategy-build tool is available (so we're in the backtest builder, not
 * creating a dashboard panel). Read-only and edit intents pay nothing.
 */
export function buildGroundingContext(ctx: GroundingContext): string {
  if (ctx.intent !== 'create') return ''
  if (
    !ctx.toolNames.includes('create_strategy_from_recipe') &&
    !ctx.toolNames.includes('create_saved_strategy')
  )
    return ''
  return serializeGroundingRecipes(selectGroundingRecipes(ctx.intentText))
}
